<script>
import {mapState, mapActions, mapGetters} from 'vuex';
import PaginationControl from '../PaginationControl/PaginationControl.vue';
import ManageAllocationFormAccept from '../ManageAllocationFormAccept/ManageAllocationFormAccept.vue';
import Loading from '../Loading/Loading.vue';
import {calculateNewPeriodFromTo, isSoldOut} from "../../services/DateToolService";
import moment from 'moment';

export default {
    components: {
        PaginationControl,
        ManageAllocationFormAccept,
        Loading
    },
    data() {
        return {
            blockingPeriod: null
        }
    },
    computed: {
        ...mapState({
            loading: state => state.allocation.allocations.loading,
            updating: state => state.allocation.modal.overlay.loading,
            page: state => state.allocation.modal.list.page,
            adding: state => state.allocation.modal.adding,
            calendarFrom: state => state.allocation.calendar.from,
            calendarTo: state => state.allocation.calendar.to,
            user: state => state.auth.user
        }),
        ...mapGetters({
            allocationsInPeriod: "allocation/allocationsInPeriodPaginated",
            allocationsInPeriodMaxPages: 'allocation/allocationsInPeriodMaxPages',
            allotmentInPeriod: "allocation/allotmentInPeriod"
        })
    },
    methods: {
        ...mapActions({
            incrementModalPagination: 'allocation/incrementModalPagination',
            decrementModalPagination: 'allocation/decrementModalPagination',
            chooseAllocation: 'allocation/chooseAllocation',
            blockAllocation: 'allocation/blockAllocation',
            addNewAllocation: 'allocation/toggleModalNewAllocation',
            getAllocationList: 'allocation/getAllocationList',
            getAllocationListMerged: 'allocation/getAllocationListMerged',
            getAllocationCalendar: 'allocation/getAllocationCalendar',
            submit: 'allocation/submitForm',
        }),
        newAllocation(allocation) {
            // Convert the data
            let newAllocation = calculateNewPeriodFromTo(this.calendarFrom, this.calendarTo, allocation);
            this.addNewAllocation(newAllocation);
        },
        block(allocation) {
            this.blockingPeriod = calculateNewPeriodFromTo(this.calendarFrom, this.calendarTo, allocation);
            this.submit([this.blockingPeriod]);
        },
        acceptBlock(allocation) {
            // Convert the data
            let blockAllocation = {allocation: allocation, ...this.blockingPeriod};
            this.blockAllocation(blockAllocation).then(() => {
                this.fetchData();
            });
        },
        beforeCalendarFrom(date) {
            const calendarFrom = moment(this.calendarFrom, 'DD/MM/YYYY');
            return moment(date).diff(calendarFrom) > 0;
        },
        afterCalendarTo(date) {
            const calendarTo = moment(this.calendarTo, 'DD/MM/YYYY');
            return moment(date).diff(calendarTo) < 0;
        },
        fetchData() {
            Promise.all([
                this.getAllocationList(),
                this.getAllocationListMerged(),
                this.getAllocationCalendar()
            ]);
        },
        canBlock (allocation) {
            return (!allocation.isReleased && this.user.contractManager && allocation.allocation.type !== 'BLOCKED')
                || (!allocation.isReleased
                    && (allocation.allocation.type === 'FREESALE' || allocation.allocation.type === 'REQUEST'))
                || ((allocation.isReleased || isSoldOut(this.calendarFrom, this.calendarTo, allocation))
                    && allocation.allocation.releaseType !== 'BLOCKED' && allocation.allocation.type !== 'BLOCKED')
        },
        blockReleased (allocation) {
            return (!allocation.isReleased && !this.user.contractManager
                    && (allocation.allocation.type === 'ALLOTMENT' || allocation.allocation.type === 'GUARANTEE'))

        }
    }
}
</script>
<template>
    <div v-if="!adding && allocationsInPeriod.length > 0" class="manage-allocation-modal-list">
        <loading v-if="updating || loading"/>
        <transition name="fade" mode="out-in">
            <div v-if="!loading" :key="'content'" class="manage-allocation-modal-list__content">
                <div class="manage-allocation-modal-list__header">
                    <div class="manage-allocation-modal-list__heading">
                        <span v-if="allocationsInPeriod.length > 0 && allotmentInPeriod">
                            {{ $t('manageAllocationModalList.header.bookingsInPeriod') }}
                        </span>
                        <span v-if="allocationsInPeriod.length === 0 || !allotmentInPeriod">
                            {{ $t('manageAllocationModalList.header.noBookingsInPeriod') }}
                        </span>
                    </div>
                </div>
                <table class="manage-allocation-modal-list__table">
                    <thead>
                        <tr>
                            <th>{{ $t('manageAllocationList.list.from') }}</th>
                            <th>{{ $t('manageAllocationList.list.to') }}</th>
                            <th>{{ $t('manageAllocationList.list.type') }}</th>
                            <th>{{ $t('manageAllocationList.list.release') }}</th>
                            <th>{{ $t('manageAllocationList.list.releaseType') }}</th>
                            <th/>
                            <th/>
                            <th/>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(element, index) in allocationsInPeriod" :key="index">
                            <td v-if="beforeCalendarFrom(element.allocation.from)">
                                {{ element.allocation.from | moment("DD/MM/YYYY") }}
                            </td>
                            <td v-if="!beforeCalendarFrom(element.allocation.from)">
                                {{ calendarFrom }}
                            </td>
                            <td v-if="afterCalendarTo(element.allocation.to)">
                                {{ element.allocation.to | moment("DD/MM/YYYY") }}
                            </td>
                            <td v-if="!afterCalendarTo(element.allocation.to)">
                                {{ calendarTo }}
                            </td>
                            <td>
                                {{ $t('manageAllocationModal.form.type.' + element.allocation.type.toLowerCase()) }}
                            </td>
                            <td v-if="element.allocation.releaseDays">
                                {{ element.allocation.releaseDays }} {{ $t('manageAllocationModalList.days') }}
                            </td>
                            <td v-if="element.allocation.releaseDate">
                                {{ element.allocation.releaseDate | moment('DD/MM/YYYY') }}
                            </td>
                            <td v-if="!element.allocation.releaseDate && !element.allocation.releaseDays">
                                {{ $t('manageAllocationModalList.releaseType.null' ) }}
                            </td>
                            <td v-if="element.allocation.releaseType">
                                {{ $t('manageAllocationModalList.releaseType.' + element.allocation.releaseType.toLowerCase() ) }}
                            </td>
                            <td v-if="!element.allocation.releaseType">
                                {{ $t('manageAllocationModalList.releaseType.null' ) }}
                            </td>
                            <td>
                                <button v-if="element.allocation.type !== 'BLOCKED'"
                                        :disabled="updating"
                                        class="manage-allocation-modal-list__btn manage-allocation-modal-list__btn-edit"
                                        @click="chooseAllocation(element.allocation)">
                                    <i v-if="element.allocation.type !== 'BLOCKED' && element.hasRequest"
                                       class="fa fa-exclamation-triangle manage-allocation-modal-list__edit-requests"/>
                                    {{ $t('manageAllocationModalList.buttons.edit' ) }}
                                </button>
                                <span v-if="element.allocation.type === 'BLOCKED'">
                                    {{ $t('manageAllocationModalList.block.blocked') }}
                                </span>
                            </td>
                            <td>
                                <button v-if="element.isReleased || element.allocation.type === 'BLOCKED'"
                                        :disabled="updating"
                                        class="manage-allocation-modal-list__btn manage-allocation-modal-list__btn-new"
                                        @click="newAllocation(element.allocation)">
                                    {{ $t('manageAllocationModalList.buttons.new' ) }}
                                </button>
                                <span v-if="!element.isReleased && element.allocation.type !== 'BLOCKED'">
                                    {{ $t('manageAllocationModalList.block.false') }}
                                </span>
                            </td>
                            <td>
                                <button v-if="canBlock(element)"
                                        :disabled="updating"
                                        class="manage-allocation-modal-list__btn manage-allocation-modal-list__btn-block"
                                        @click="block(element.allocation)">
                                    {{ $t('manageAllocationModalList.buttons.block' ) }}
                                </button>
                                <span v-if="blockReleased(element)">
                                    {{ $t('manageAllocationModalList.block.false') }}
                                </span>
                                <span v-if="(element.isReleased && element.allocation.releaseType === 'BLOCKED') || element.allocation.type === 'BLOCKED'">
                                    {{ $t('manageAllocationModalList.block.blocked') }}
                                </span>
                                <manage-allocation-form-accept :label="'Block allocations'"
                                                               :method="'block'"
                                                               @accept="acceptBlock(element.allocation)"/>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div class="manage-allocation-modal-list__footer">
                    <div v-if="allocationsInPeriod.length > 0" class="manage-allocation-modal-list__pagination">
                        <pagination-control :current-page="page"
                                            :max-pages="allocationsInPeriodMaxPages"
                                            @prev="decrementModalPagination"
                                            @next="incrementModalPagination"/>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>



// WEBPACK FOOTER //
// src/components/ManageAllocationModalList/ManageAllocationModalList.vue